import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import styles from './styles.module.css';

type TabProps = {
  label: string;
  children: React.ReactNode;
  tabContentClassName?: string;
};

type TabsProps = {
  children: React.ReactNode;
  tabsContainerClassName?: string;
  tabListClassName?: string;
  tabButtonClassName?: string;
  activeTabButtonClassName?: string;
  tabContentContainerClassName?: string;
  activeTabIndex?: number;
  onTabChange?: (index: number) => void; // Optional callback to handle tab changes
};

export const Tab: React.FC<TabProps> = ({ label, children, tabContentClassName }) => {
  return <div className={clsx(styles.tabContent, tabContentClassName)}>{children}</div>;
};

const Tabs: React.FC<TabsProps> = ({
  children,
  tabsContainerClassName,
  tabListClassName,
  tabButtonClassName,
  activeTabButtonClassName,
  tabContentContainerClassName,
  activeTabIndex = 0,
  onTabChange, // Optional callback
}) => {
  const [activeTab, setActiveTab] = useState(activeTabIndex);

  useEffect(() => {
    setActiveTab(activeTabIndex);
  }, [activeTabIndex]);

  const handleTabClick = (index: number) => {
    if (onTabChange) {
      // Invoke the callback if provided
      onTabChange(index);
      setActiveTab(index);
    } else {
      // Otherwise, handle the active tab state locally
      setActiveTab(index);
    }
  };

  return (
    <div className={clsx(styles.tabsContainer, tabsContainerClassName)}>
      <div className={clsx(styles.tabList, tabListClassName)}>
        {React.Children.map(children, (child, index) => {
          if (React.isValidElement(child)) {
            return (
              <button
                key={index}
                className={clsx(
                  styles.tabButton,
                  tabButtonClassName,
                  { [clsx(styles.activeTab, activeTabButtonClassName)]: activeTab === index },
                  'fontSizeSm14 fontColorSecondary'
                )}
                onClick={() => handleTabClick(index)}
              >
                {child.props.label}
              </button>
            );
          }
          return null;
        })}
      </div>
      <div className={clsx(styles.tabContentContainer, tabContentContainerClassName)}>
        {React.Children.toArray(children)[activeTab]}
      </div>
    </div>
  );
};

export default Tabs;
